import React from 'react';
import ResourceLayout from './ResourceLayout';

const LearningObjectivesGuide = () => {
  const content = (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-primary mb-4">Introduction to Learning Objectives</h2>
        <p className="text-muted-foreground mb-4">
          Learning objectives are clear, specific statements that describe what participants will be able to do, know, or demonstrate 
          by the end of a learning experience. They serve as the foundation for course design, instruction, and assessment.
        </p>
        
        <h3 className="text-xl font-semibold text-foreground mb-3">Why Learning Objectives Matter</h3>
        <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-6">
          <li>Provide clear direction for course design and content selection</li>
          <li>Help participants understand expectations and goals</li>
          <li>Guide assessment and evaluation strategies</li>
          <li>Ensure alignment between activities and desired outcomes</li>
          <li>Facilitate measurement of learning success</li>
        </ul>
      </div>

      <div>
        <h2 className="text-2xl font-bold text-primary mb-4">Components of Effective Learning Objectives</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="border rounded-lg p-4">
            <h3 className="font-semibold text-foreground mb-2">ABCD Format</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><strong>A</strong>udience: Who is learning?</li>
              <li><strong>B</strong>ehavior: What will they do?</li>
              <li><strong>C</strong>ondition: Under what circumstances?</li>
              <li><strong>D</strong>egree: How well must they perform?</li>
            </ul>
          </div>
          <div className="border rounded-lg p-4">
            <h3 className="font-semibold text-foreground mb-2">Key Characteristics</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>Specific and measurable</li>
              <li>Achievable and realistic</li>
              <li>Relevant to participant needs</li>
              <li>Time-bound when appropriate</li>
              <li>Observable behaviors</li>
            </ul>
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-bold text-primary mb-4">Bloom's Taxonomy Action Verbs</h2>
        <table className="w-full border-collapse border border-gray-300 mb-6">
          <thead>
            <tr className="bg-gray-50">
              <th className="border border-gray-300 p-3 text-left font-semibold">Cognitive Level</th>
              <th className="border border-gray-300 p-3 text-left font-semibold">Action Verbs</th>
              <th className="border border-gray-300 p-3 text-left font-semibold">Example Objective</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 p-3 font-medium">Remember</td>
              <td className="border border-gray-300 p-3">Define, list, recall, identify, name</td>
              <td className="border border-gray-300 p-3 text-sm">List the five steps of the problem-solving process</td>
            </tr>
            <tr>
              <td className="border border-gray-300 p-3 font-medium">Understand</td>
              <td className="border border-gray-300 p-3">Explain, describe, summarize, interpret</td>
              <td className="border border-gray-300 p-3 text-sm">Explain the relationship between leadership styles and team performance</td>
            </tr>
            <tr>
              <td className="border border-gray-300 p-3 font-medium">Apply</td>
              <td className="border border-gray-300 p-3">Demonstrate, use, implement, execute</td>
              <td className="border border-gray-300 p-3 text-sm">Demonstrate effective conflict resolution techniques in role-play scenarios</td>
            </tr>
            <tr>
              <td className="border border-gray-300 p-3 font-medium">Analyze</td>
              <td className="border border-gray-300 p-3">Compare, contrast, examine, categorize</td>
              <td className="border border-gray-300 p-3 text-sm">Analyze case studies to identify communication barriers and solutions</td>
            </tr>
            <tr>
              <td className="border border-gray-300 p-3 font-medium">Evaluate</td>
              <td className="border border-gray-300 p-3">Assess, judge, critique, recommend</td>
              <td className="border border-gray-300 p-3 text-sm">Evaluate the effectiveness of different training methods for adult learners</td>
            </tr>
            <tr>
              <td className="border border-gray-300 p-3 font-medium">Create</td>
              <td className="border border-gray-300 p-3">Design, develop, construct, plan</td>
              <td className="border border-gray-300 p-3 text-sm">Design a comprehensive onboarding program for new employees</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div>
        <h2 className="text-2xl font-bold text-primary mb-4">Learning Objective Templates</h2>
        
        <div className="space-y-6">
          <div className="border rounded-lg p-4">
            <h3 className="font-semibold text-foreground mb-3">Knowledge-Based Objectives</h3>
            <div className="bg-gray-50 p-3 rounded mb-3">
              <p className="text-sm font-mono">By the end of this [session/module/course], participants will be able to [action verb] [content/skill] [condition] [degree of mastery].</p>
            </div>
            <p className="text-sm text-muted-foreground mb-2"><strong>Example:</strong></p>
            <p className="text-sm italic">By the end of this module, participants will be able to identify all seven principles of effective communication with 100% accuracy.</p>
          </div>

          <div className="border rounded-lg p-4">
            <h3 className="font-semibold text-foreground mb-3">Skill-Based Objectives</h3>
            <div className="bg-gray-50 p-3 rounded mb-3">
              <p className="text-sm font-mono">Given [condition/context], participants will [action verb] [skill/behavior] [standard/criteria].</p>
            </div>
            <p className="text-sm text-muted-foreground mb-2"><strong>Example:</strong></p>
            <p className="text-sm italic">Given a challenging customer scenario, participants will demonstrate active listening skills by paraphrasing customer concerns and asking clarifying questions.</p>
          </div>

          <div className="border rounded-lg p-4">
            <h3 className="font-semibold text-foreground mb-3">Application-Based Objectives</h3>
            <div className="bg-gray-50 p-3 rounded mb-3">
              <p className="text-sm font-mono">Using [tools/resources], participants will [apply/implement] [knowledge/skill] to [real-world context] [success criteria].</p>
            </div>
            <p className="text-sm text-muted-foreground mb-2"><strong>Example:</strong></p>
            <p className="text-sm italic">Using the project management framework, participants will create a detailed project plan that includes timeline, resources, and risk mitigation strategies.</p>
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-bold text-primary mb-4">Common Mistakes to Avoid</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="border border-red-200 rounded-lg p-4 bg-red-50">
            <h3 className="font-semibold text-red-800 mb-3">❌ Avoid These</h3>
            <ul className="space-y-2 text-sm text-red-700">
              <li>Vague verbs (understand, know, appreciate)</li>
              <li>Too many objectives per session</li>
              <li>Unrealistic expectations</li>
              <li>Focusing on teaching activities vs. learning outcomes</li>
              <li>Missing measurable criteria</li>
            </ul>
          </div>
          <div className="border border-green-200 rounded-lg p-4 bg-green-50">
            <h3 className="font-semibold text-green-800 mb-3">✅ Best Practices</h3>
            <ul className="space-y-2 text-sm text-green-700">
              <li>Use specific, observable action verbs</li>
              <li>Limit to 3-5 objectives per session</li>
              <li>Align with participant needs and context</li>
              <li>Focus on participant outcomes</li>
              <li>Include clear success criteria</li>
            </ul>
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-bold text-primary mb-4">Learning Objective Worksheet</h2>
        <div className="border rounded-lg p-6 space-y-6">
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">Course/Session Topic:</label>
            <div className="border-b border-gray-300 pb-1 mb-4 min-h-[24px]"></div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">Target Audience:</label>
            <div className="border-b border-gray-300 pb-1 mb-4 min-h-[24px]"></div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">Learning Objective 1:</label>
            <div className="border-b border-gray-300 pb-1 mb-2 min-h-[24px]"></div>
            <div className="text-xs text-muted-foreground mb-4">
              By the end of this _______, participants will be able to _______ [action verb] _______ [content] _______ [condition] _______ [degree].
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">Learning Objective 2:</label>
            <div className="border-b border-gray-300 pb-1 mb-2 min-h-[24px]"></div>
            <div className="text-xs text-muted-foreground mb-4">
              By the end of this _______, participants will be able to _______ [action verb] _______ [content] _______ [condition] _______ [degree].
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">Learning Objective 3:</label>
            <div className="border-b border-gray-300 pb-1 mb-2 min-h-[24px]"></div>
            <div className="text-xs text-muted-foreground mb-4">
              By the end of this _______, participants will be able to _______ [action verb] _______ [content] _______ [condition] _______ [degree].
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">Assessment Strategy:</label>
            <div className="border-b border-gray-300 pb-1 mb-2 min-h-[24px]"></div>
            <div className="text-xs text-muted-foreground">
              How will you measure achievement of these objectives?
            </div>
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-bold text-primary mb-4">Quick Reference Checklist</h2>
        <div className="border rounded-lg p-4">
          <h3 className="font-semibold text-foreground mb-3">Before finalizing your learning objectives, ensure:</h3>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <input type="checkbox" className="form-checkbox" />
              <span className="text-sm">Each objective uses a specific, measurable action verb</span>
            </div>
            <div className="flex items-center space-x-2">
              <input type="checkbox" className="form-checkbox" />
              <span className="text-sm">Objectives are achievable within the time frame</span>
            </div>
            <div className="flex items-center space-x-2">
              <input type="checkbox" className="form-checkbox" />
              <span className="text-sm">Success criteria are clearly defined</span>
            </div>
            <div className="flex items-center space-x-2">
              <input type="checkbox" className="form-checkbox" />
              <span className="text-sm">Objectives align with participant needs and job requirements</span>
            </div>
            <div className="flex items-center space-x-2">
              <input type="checkbox" className="form-checkbox" />
              <span className="text-sm">Assessment methods are planned for each objective</span>
            </div>
            <div className="flex items-center space-x-2">
              <input type="checkbox" className="form-checkbox" />
              <span className="text-sm">Language is clear and free of jargon</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <ResourceLayout
      title="Learning Objectives Guide"
      description="Step-by-Step Guide to Writing Effective Learning Objectives"
      content={content}
    />
  );
};

export default LearningObjectivesGuide;